/**
 * Admin Error Dashboard — 5-Tab Fehler-Monitoring
 * 
 * Tabs:
 * 1. Voice / WebSocket — Verbindungsabbrüche, ASR/TTS-Fehler
 * 2. Parsing & Datenqualität — PDF/CSV/XLSX-Fehler, Score-Extraktion
 * 3. LLM / KI-Coach — API-Fehler, Timeouts, Rate-Limits
 * 4. System & Infrastruktur — DB, S3, Webhooks, Server-Neustarts
 * 5. Nutzer-Experience — Client-Fehler, Onboarding-Abbrüche
 * 
 * Features:
 * - Ampel-Übersicht (grün/gelb/rot) pro Kategorie
 * - Hover-Details: Wer hatte das Problem zuletzt?
 * - Fix-Vorschläge pro Fehlertyp
 * - Timeline-Chart pro Kategorie
 * - Event-Liste mit Pagination
 */

import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  RefreshCw,
  Mic,
  FileText,
  Brain,
  Server,
  UserX,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Clock,
  User,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Info,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

// ─── Design Tokens (matching AppAdmin) ───
const T = {
  olive: "#6b7f4e",
  oliveLight: "#6b7f4e15",
  oliveMed: "#6b7f4e25",
  dark: "#2d3a1e",
  mid: "#5a6b4a",
  muted: "#8a9680",
  border: "#d4dcc4",
  bg: "#f7f4ef",
  white: "#ffffff",
  saffron: "#c8a45e",
  saffronLight: "#c8a45e15",
};

// ─── Category Config ───
type CategoryId = "voice" | "parsing" | "llm" | "system" | "ux";

const CATEGORIES: { id: CategoryId; label: string; icon: typeof Mic; description: string }[] = [
  { id: "voice", label: "Voice / WebSocket", icon: Mic, description: "Verbindungsabbrüche, ASR/TTS-Fehler, Session-Dauer" },
  { id: "parsing", label: "Parsing & Daten", icon: FileText, description: "PDF/CSV/XLSX-Fehler, Score-Extraktion, fehlende Daten" },
  { id: "llm", label: "LLM / KI-Coach", icon: Brain, description: "API-Fehler, Timeouts, Rate-Limits, Kontext-Limits" },
  { id: "system", label: "System & Infra", icon: Server, description: "DB, S3, Webhooks, Server-Neustarts" },
  { id: "ux", label: "Nutzer-Experience", icon: UserX, description: "Client-Fehler, Onboarding-Abbrüche, Session-Probleme" },
];

// ─── Severity Colors ───
const SEVERITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  critical: { bg: "#fee2e2", text: "#dc2626", border: "#fca5a5" },
  warning: { bg: "#fef3c7", text: "#d97706", border: "#fcd34d" },
  info: { bg: "#dbeafe", text: "#2563eb", border: "#93c5fd" },
};

const TRAFFIC_COLORS: Record<string, { bg: string; glow: string; text: string }> = {
  green: { bg: "#22c55e", glow: "#22c55e40", text: "Alles OK" },
  yellow: { bg: "#eab308", glow: "#eab30840", text: "Warnungen" },
  red: { bg: "#ef4444", glow: "#ef444440", text: "Kritisch" },
};

// ─── Helper: format relative time ───
function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return "gerade eben";
  if (diff < 3_600_000) return `vor ${Math.floor(diff / 60_000)} Min.`;
  if (diff < 86_400_000) return `vor ${Math.floor(diff / 3_600_000)} Std.`;
  return `vor ${Math.floor(diff / 86_400_000)} Tagen`;
}

function formatDateTime(date: Date | string | number): string {
  const d = new Date(date);
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ═══════════════════════════════════════════════════════════
//  TRAFFIC LIGHT INDICATOR
// ═══════════════════════════════════════════════════════════

function TrafficLight({ status }: { status: "green" | "yellow" | "red" }) {
  const c = TRAFFIC_COLORS[status];
  return (
    <div className="relative flex items-center justify-center">
      <div
        className="w-4 h-4 rounded-full"
        style={{ backgroundColor: c.bg, boxShadow: `0 0 8px ${c.glow}` }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  MINI TIMELINE BAR CHART
// ═══════════════════════════════════════════════════════════

function MiniTimeline({ category, hoursBack }: { category: CategoryId; hoursBack: number }) {
  // @ts-ignore
  const { data, isLoading } = trpc.errorDashboard.getTimeline.useQuery(
    { category, hoursBack },
    { refetchInterval: 60_000 }
  );

  if (isLoading || !data?.timeline?.length) {
    return <div className="h-16 flex items-center justify-center text-xs" style={{ color: T.muted }}>Keine Daten</div>;
  }

  const timeline = data.timeline;
  const maxVal = Math.max(...timeline.map((t: any) => t.critical + t.warning + t.info), 1);
  const barWidth = Math.max(2, Math.floor(100 / Math.min(timeline.length, 48)));

  return (
    <div className="h-16 flex items-end gap-px overflow-hidden">
      {timeline.slice(-48).map((t: any, i: number) => {
        const total = t.critical + t.warning + t.info;
        const height = Math.max(2, (total / maxVal) * 56);
        const color = t.critical > 0 ? "#ef4444" : t.warning > 0 ? "#eab308" : "#22c55e";
        return (
          <div
            key={i}
            className="rounded-t-sm transition-all"
            style={{
              width: `${barWidth}%`,
              height: `${height}px`,
              backgroundColor: color,
              opacity: total === 0 ? 0.15 : 0.8,
            }}
            title={`${t.hour}: ${total} Events (${t.critical} krit., ${t.warning} warn.)`}
          />
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  AFFECTED USERS POPOVER (Hover Detail)
// ═══════════════════════════════════════════════════════════

function AffectedUsersPopover({ eventType, hoursBack }: { eventType: string; hoursBack: number }) {
  const [isOpen, setIsOpen] = useState(false);
  // @ts-ignore
  const { data, isLoading } = trpc.errorDashboard.getAffectedUsers.useQuery(
    { eventType, hoursBack },
    { enabled: isOpen }
  );

  return (
    <div className="relative" onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)}>
      <button className="flex items-center gap-1 text-xs underline decoration-dotted" style={{ color: T.olive }}>
        <User className="w-3 h-3" />
        Betroffene
      </button>
      {isOpen && (
        <div
          className="absolute z-50 left-0 top-6 w-72 rounded-lg shadow-xl border p-3"
          style={{ background: T.white, borderColor: T.border }}
        >
          <div className="text-xs font-semibold mb-2" style={{ color: T.dark }}>
            Betroffene Nutzer (letzte {hoursBack}h)
          </div>
          {isLoading ? (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="w-3 h-3 animate-spin" style={{ color: T.olive }} />
              <span className="text-xs" style={{ color: T.muted }}>Lade...</span>
            </div>
          ) : !data?.users?.length ? (
            <div className="text-xs py-2" style={{ color: T.muted }}>Keine Nutzer betroffen</div>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {data.users.map((u: any, i: number) => (
                <div key={i} className="flex items-center justify-between text-xs rounded px-2 py-1.5" style={{ background: T.bg }}>
                  <div>
                    <span className="font-medium" style={{ color: T.dark }}>{u.clientName}</span>
                    {u.clientId && (
                      <span className="ml-1" style={{ color: T.muted }}>({u.clientId.slice(0, 8)}...)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono" style={{ color: T.mid }}>{u.occurrences}x</span>
                    <span style={{ color: T.muted }}>{timeAgo(u.lastSeen)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  EVENT TYPE SUMMARY ROW (with fix suggestion)
// ═══════════════════════════════════════════════════════════

function EventTypeSummaryRow({
  type,
  hoursBack,
}: {
  type: {
    eventType: string;
    label: string;
    severity: string;
    count: number;
    uniqueClients: number;
    lastSeen: number;
    fixSuggestion: string;
  };
  hoursBack: number;
}) {
  const [showFix, setShowFix] = useState(false);
  const sc = SEVERITY_COLORS[type.severity] || SEVERITY_COLORS.info;

  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: sc.border }}>
      <div className="flex items-center gap-3 px-4 py-3" style={{ background: `${sc.bg}40` }}>
        {/* Severity Icon */}
        <div className="flex-shrink-0">
          {type.severity === "critical" ? (
            <XCircle className="w-5 h-5" style={{ color: sc.text }} />
          ) : type.severity === "warning" ? (
            <AlertTriangle className="w-5 h-5" style={{ color: sc.text }} />
          ) : (
            <Info className="w-5 h-5" style={{ color: sc.text }} />
          )}
        </div>

        {/* Label & Type */}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm" style={{ color: T.dark }}>{type.label}</div>
          <div className="text-xs font-mono" style={{ color: T.muted }}>{type.eventType}</div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 text-xs flex-shrink-0">
          <div className="text-center">
            <div className="font-bold text-lg" style={{ color: sc.text }}>{type.count}</div>
            <div style={{ color: T.muted }}>Events</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-lg" style={{ color: T.mid }}>{type.uniqueClients}</div>
            <div style={{ color: T.muted }}>Nutzer</div>
          </div>
          <div className="text-center">
            <div className="text-xs" style={{ color: T.muted }}>
              <Clock className="w-3 h-3 inline mr-1" />
              {timeAgo(type.lastSeen)}
            </div>
          </div>
        </div>

        {/* Affected Users Hover */}
        <AffectedUsersPopover eventType={type.eventType} hoursBack={hoursBack} />

        {/* Fix Suggestion Toggle */}
        <button
          onClick={() => setShowFix(!showFix)}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors"
          style={{
            background: showFix ? T.oliveMed : "transparent",
            color: T.olive,
          }}
        >
          <Lightbulb className="w-3.5 h-3.5" />
          Fix
          {showFix ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {/* Fix Suggestion Panel */}
      {showFix && (
        <div className="px-4 py-3 border-t text-sm" style={{ borderColor: sc.border, background: T.oliveLight, color: T.mid }}>
          <div className="flex items-start gap-2">
            <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: T.saffron }} />
            <div>
              <div className="font-medium text-xs mb-1" style={{ color: T.dark }}>Empfohlener Fix:</div>
              <div className="text-xs leading-relaxed">{type.fixSuggestion}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  EVENT LOG TABLE
// ═══════════════════════════════════════════════════════════

function EventLogTable({ category, hoursBack }: { category: CategoryId; hoursBack: number }) {
  const [severity, setSeverity] = useState<"all" | "info" | "warning" | "critical">("all");
  const [offset, setOffset] = useState(0);
  const limit = 20;

  // @ts-ignore
  const { data, isLoading, refetch } = trpc.errorDashboard.getEvents.useQuery(
    { category, severity, hoursBack, limit, offset },
    { refetchInterval: 30_000 }
  );

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-3">
        <Select value={severity} onValueChange={(v) => { setSeverity(v as any); setOffset(0); }}>
          <SelectTrigger className="w-40 h-8 text-xs" style={{ borderColor: T.border }}>
            <SelectValue placeholder="Schweregrad" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Schweregrade</SelectItem>
            <SelectItem value="critical">Kritisch</SelectItem>
            <SelectItem value="warning">Warnung</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs" style={{ color: T.muted }}>
          {data?.total ?? 0} Events gesamt
        </div>
      </div>

      {/* Event List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.olive }} />
        </div>
      ) : !data?.events?.length ? (
        <div className="text-center py-8">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2" style={{ color: "#22c55e" }} />
          <div className="text-sm" style={{ color: T.muted }}>Keine Events in diesem Zeitraum</div>
        </div>
      ) : (
        <div className="space-y-1.5">
          {data.events.map((e: any) => {
            const sc = SEVERITY_COLORS[e.severity] || SEVERITY_COLORS.info;
            return (
              <div
                key={e.id}
                className="flex items-start gap-3 rounded-lg px-3 py-2 text-xs border"
                style={{ borderColor: `${sc.border}60`, background: `${sc.bg}30` }}
              >
                <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: sc.text }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-medium" style={{ color: T.dark }}>{e.eventType}</span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0" style={{ borderColor: sc.border, color: sc.text }}>
                      {e.severity}
                    </Badge>
                    {e.clientName && (
                      <span style={{ color: T.olive }}>
                        <User className="w-3 h-3 inline mr-0.5" />
                        {e.clientName}
                      </span>
                    )}
                  </div>
                  <div style={{ color: T.mid }}>{e.message}</div>
                  {e.details && (
                    <details className="mt-1">
                      <summary className="cursor-pointer" style={{ color: T.muted }}>Details</summary>
                      <pre className="mt-1 text-[10px] p-2 rounded overflow-x-auto" style={{ background: T.bg, color: T.mid }}>
                        {typeof e.details === "string" ? e.details : JSON.stringify(e.details, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
                <div className="flex-shrink-0 text-right" style={{ color: T.muted }}>
                  {formatDateTime(e.createdAt)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {data && data.total > limit && (
        <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: T.border }}>
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
            className="text-xs"
          >
            Zurück
          </Button>
          <span className="text-xs" style={{ color: T.muted }}>
            {offset + 1}–{Math.min(offset + limit, data.total)} von {data.total}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + limit >= data.total}
            onClick={() => setOffset(offset + limit)}
            className="text-xs"
          >
            Weiter
          </Button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  CATEGORY DETAIL VIEW
// ═══════════════════════════════════════════════════════════

function CategoryDetail({ category, hoursBack }: { category: CategoryId; hoursBack: number }) {
  // @ts-ignore
  const { data: typeSummary, isLoading } = trpc.errorDashboard.getEventTypeSummary.useQuery(
    { category, hoursBack },
    { refetchInterval: 60_000 }
  );

  return (
    <div className="space-y-6">
      {/* Timeline Chart */}
      <Card style={{ borderColor: T.border }}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm" style={{ color: T.dark }}>
            Zeitverlauf (letzte {hoursBack}h)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <MiniTimeline category={category} hoursBack={hoursBack} />
        </CardContent>
      </Card>

      {/* Event Type Summary */}
      <div>
        <h3 className="text-sm font-semibold mb-3" style={{ color: T.dark }}>
          Fehlertypen-Übersicht
        </h3>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.olive }} />
          </div>
        ) : !typeSummary?.types?.length ? (
          <div className="text-center py-8">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2" style={{ color: "#22c55e" }} />
            <div className="text-sm" style={{ color: T.muted }}>Keine Fehler in diesem Zeitraum</div>
          </div>
        ) : (
          <div className="space-y-2">
            {typeSummary.types.map((type: any) => (
              <EventTypeSummaryRow key={`${type.eventType}-${type.severity}`} type={type} hoursBack={hoursBack} />
            ))}
          </div>
        )}
      </div>

      {/* Event Log */}
      <div>
        <h3 className="text-sm font-semibold mb-3" style={{ color: T.dark }}>
          Event-Log
        </h3>
        <EventLogTable category={category} hoursBack={hoursBack} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  MAIN ERROR DASHBOARD TAB
// ═══════════════════════════════════════════════════════════

export default function ErrorDashboardTab() {
  const [activeCategory, setActiveCategory] = useState<CategoryId | null>(null);
  const [hoursBack, setHoursBack] = useState(24);

  // @ts-ignore - tRPC types are generated at runtime
  const { data: overview, isLoading, refetch } = trpc.errorDashboard.overview.useQuery(undefined, {
    refetchInterval: 30_000,
  }) as any;

  // @ts-ignore - tRPC types are generated at runtime
  const cleanupMutation = trpc.errorDashboard.cleanup.useMutation({
    onSuccess: (result: any) => {
      toast.success(`${result.deleted} alte Events gelöscht`);
      refetch();
    },
    onError: () => toast.error("Bereinigung fehlgeschlagen"),
  }) as any;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ color: T.dark, fontFamily: "'Playfair Display', serif" }}>
            Fehler-Dashboard
          </h2>
          <p className="text-xs mt-0.5" style={{ color: T.muted }}>
            Echtzeit-Monitoring aller Systemkomponenten
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(hoursBack)} onValueChange={(v) => setHoursBack(Number(v))}>
            <SelectTrigger className="w-36 h-8 text-xs" style={{ borderColor: T.border }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Letzte Stunde</SelectItem>
              <SelectItem value="6">Letzte 6 Stunden</SelectItem>
              <SelectItem value="24">Letzte 24 Stunden</SelectItem>
              <SelectItem value="72">Letzte 3 Tage</SelectItem>
              <SelectItem value="168">Letzte 7 Tage</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="h-8 text-xs"
            style={{ borderColor: T.border }}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            Aktualisieren
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => cleanupMutation.mutate({ olderThanDays: 30 })}
            disabled={cleanupMutation.isPending}
            className="h-8 text-xs"
            style={{ borderColor: T.border }}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            Bereinigen
          </Button>
        </div>
      </div>

      {/* Overview: Traffic Light Cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: T.olive }} />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {CATEGORIES.map((cat) => {
            const catData = overview?.categories?.find((c: any) => c.label === cat.label);
            const status = catData?.status ?? "green";
            const count = catData?.count24h ?? 0;
            const critCount = catData?.criticalCount ?? 0;
            const isActive = activeCategory === cat.id;
            const tc = TRAFFIC_COLORS[status];

            return (
              <Card
                key={cat.id}
                className={`cursor-pointer transition-all hover:shadow-md ${isActive ? "ring-2" : ""}`}
                style={{
                  borderColor: isActive ? T.olive : T.border,
                  // @ts-ignore
                  '--tw-ring-color': T.olive,
                } as React.CSSProperties}
                onClick={() => setActiveCategory(isActive ? null : cat.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <cat.icon className="w-5 h-5" style={{ color: T.olive }} />
                    <TrafficLight status={status} />
                  </div>
                  <div className="text-sm font-semibold mb-0.5" style={{ color: T.dark }}>
                    {cat.label}
                  </div>
                  <div className="text-xs" style={{ color: T.muted }}>
                    {count === 0 ? (
                      <span style={{ color: "#22c55e" }}>Keine Events</span>
                    ) : (
                      <>
                        <span className="font-mono font-bold" style={{ color: critCount > 0 ? "#ef4444" : T.mid }}>
                          {count}
                        </span>
                        {" Events"}
                        {critCount > 0 && (
                          <span style={{ color: "#ef4444" }}> ({critCount} kritisch)</span>
                        )}
                      </>
                    )}
                  </div>
                  {/* Mini timeline in card */}
                  <div className="mt-2">
                    <MiniTimeline category={cat.id} hoursBack={hoursBack} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Category Detail View */}
      {activeCategory && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveCategory(null)}
              className="text-xs"
              style={{ color: T.olive }}
            >
              ← Zurück zur Übersicht
            </Button>
            <span className="text-sm font-semibold" style={{ color: T.dark }}>
              {CATEGORIES.find(c => c.id === activeCategory)?.label}
            </span>
          </div>
          <CategoryDetail category={activeCategory} hoursBack={hoursBack} />
        </div>
      )}
    </div>
  );
}
